import { memo } from 'react'

export interface StatusDisplayProps {
  status: string
  lastLocation: string
  heading: number | null
  lastPOI: string
  lastSpoken: string
}

export const StatusDisplay = memo(({
  status,
  lastLocation,
  heading,
  lastPOI,
  lastSpoken,
}: StatusDisplayProps) => {
  return (
    <div className="grid gap-2.5 sm:gap-3" role="status" aria-live="polite" aria-atomic="false">
      <div className="p-2.5 sm:p-3 bg-background-elevated rounded-lg border border-border">
        <div className="text-xs text-text-muted mb-1">Status</div>
        <div className="text-sm sm:text-base font-semibold text-text break-words" aria-label={`Current status: ${status}`}>
          {status}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
        <div className="p-2 sm:p-2.5 bg-background-elevated rounded-lg border border-border">
          <div className="text-xs text-text-muted mb-1">Location</div>
          <div className="text-xs sm:text-sm font-mono text-text truncate" aria-label={`Last location: ${lastLocation}`}>
            {lastLocation}
          </div>
        </div>
        <div className="p-2 sm:p-2.5 bg-background-elevated rounded-lg border border-border">
          <div className="text-xs text-text-muted mb-1">Heading</div>
          <div className="text-xs sm:text-sm font-medium text-text" aria-label={`Heading: ${heading == null ? 'unknown' : `${Math.round(heading)} degrees`}`}>
            {heading == null ? '—' : `${Math.round(heading)}°`}
          </div>
        </div>
      </div>

      {lastPOI !== '-' && (
        <div className="p-2.5 sm:p-3 bg-primary/10 border border-primary/30 rounded-lg">
          <div className="text-xs text-text-muted mb-1">Current POI</div>
          <div className="text-xs sm:text-sm font-semibold text-text break-words" aria-label={`Last point of interest: ${lastPOI}`}>
            {lastPOI}
          </div>
        </div>
      )}

      {lastSpoken !== '-' && (
        <div className="p-2.5 sm:p-3 bg-background-elevated rounded-lg border border-border">
          <div className="text-xs text-text-muted mb-1">Last Narration</div>
          <div 
            className="text-xs sm:text-sm text-text-muted italic break-words overflow-wrap-anywhere whitespace-pre-wrap leading-relaxed overflow-y-auto max-h-32 sm:max-h-40" 
            aria-label={`Last spoken text: ${lastSpoken}`}
            style={{ 
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {lastSpoken}
          </div>
        </div>
      )}
    </div>
  )
})

StatusDisplay.displayName = 'StatusDisplay'
